/**
 * ΔH°rxn from standard enthalpies of formation, and Hess's-law bookkeeping.
 *
 *     ΔH°rxn = Σ n·ΔfH°(products) − Σ m·ΔfH°(reactants)
 *
 * A reaction is typed as an equation: `CH4(g) + 2 O2(g) -> CO2(g) + 2 H2O(l)`. Species are
 * separated by a plus with spaces (or a plus straight after a state, `)+`), so that
 * charges such as Na+ stay intact; a leading number is the coefficient (`2`, `0.5`, `1/2`,
 * `½`); a trailing `(g)`, `(l)`, `(s)`, `(aq)` is the state; and `@value` after a species
 * supplies your own ΔfH° in kJ/mol for one that is not in the table (`C8H18(l)@-250.1`).
 * Formulas go through the
 * shared chemistry parser, so `H₂O`, `H2O` and `SO4^2-` all work and unbalanced
 * equations can be flagged.
 */

import {
  SPECIES,
  type Species,
  type State,
} from "@/lib/tool-kit/chem-reference/formation-data";
import {
  countAtoms,
  formulaToUnicode,
  molarMassOf,
  type ParsedFormula,
  parseFormula,
} from "@/lib/tool-kit/chemistry/formula";

/* ---------------------------------------------------------------- the table */

/** The canonical display and lookup form of a formula: `H₂O`, `SO₄²⁻`; null if unreadable. */
export function canonicalFormula(raw: string): string | null {
  const parsed = parseFormula(raw);
  return parsed.ok ? formulaToUnicode(parsed.formula) : null;
}

export interface TableSpecies extends Species {
  /** `formulaToUnicode` of the formula. */
  formula: string;
  /** "H₂O(l)". */
  label: string;
}

function labelOf(formula: string, state: State): string {
  return `${formula}(${state})`;
}

export const TABLE: readonly TableSpecies[] = SPECIES.map((species) => {
  const formula = canonicalFormula(species.raw) ?? species.raw;
  return { ...species, formula, label: labelOf(formula, species.state) };
});

const BY_LABEL = new Map(TABLE.map((s) => [s.label, s]));

/* ---------------------------------------------------------------- the parser */

export type TermProblem =
  | { kind: "syntax"; token: string }
  | { kind: "formula"; token: string }
  | { kind: "unknown"; token: string }
  | { kind: "ambiguous"; token: string; states: State[] }
  | { kind: "coefficient"; token: string };

export interface Term {
  coefficient: number;
  /** Canonical formula, e.g. "H₂O". */
  formula: string;
  state: State | null;
  /** The table entry (or a custom one built from `@value`), when found. */
  entry: TableSpecies | null;
  /** True when ΔfH° came from `@value` rather than the table. */
  custom: boolean;
  parsed: ParsedFormula;
}

export interface Side {
  terms: Term[];
  problems: TermProblem[];
}

const ARROW = /\s*(?:->|→|⟶|=>|=)\s*/;
const SIDE_SPLIT = /\s+\+\s+|(?<=\))\s*\+\s*/;
const STATE_SUFFIX = /\((g|l|s|aq|graphite|diamond)\)$/;
const OVERRIDE = /\s*@\s*(-?\d+(?:\.\d+)?)\s*$/;
const COEFFICIENT = /^(\d+(?:\.\d+)?(?:\/\d+)?|½)\s*/;
const MAX_COEFFICIENT = 1000;

function readCoefficient(text: string): number {
  if (text === "½") {
    return 0.5;
  }
  if (text.includes("/")) {
    const [top, bottom] = text.split("/").map(Number);
    return top / bottom;
  }
  return Number(text);
}

function lookup(
  formula: string,
  state: State | null
): Term["entry"] | "none" | State[] {
  if (state !== null) {
    return BY_LABEL.get(labelOf(formula, state)) ?? "none";
  }
  const matches = TABLE.filter((s) => s.formula === formula);
  if (matches.length === 0) {
    return "none";
  }
  return matches.length === 1 ? matches[0] : matches.map((s) => s.state);
}

function parseTerm(token: string): Term | TermProblem {
  let text = token.trim();
  const overrideMatch = OVERRIDE.exec(text);
  if (overrideMatch) {
    text = text.slice(0, overrideMatch.index);
  }
  const coefficientMatch = COEFFICIENT.exec(text);
  let coefficient = 1;
  if (coefficientMatch) {
    coefficient = readCoefficient(coefficientMatch[1]);
    text = text.slice(coefficientMatch[0].length);
  }
  if (!(coefficient > 0 && coefficient <= MAX_COEFFICIENT)) {
    return { kind: "coefficient", token };
  }
  const stateMatch = STATE_SUFFIX.exec(text);
  const state = stateMatch ? (stateMatch[1] as State) : null;
  const body = stateMatch ? text.slice(0, stateMatch.index) : text;
  if (body.trim() === "") {
    return { kind: "syntax", token };
  }
  const parsed = parseFormula(body);
  if (!parsed.ok) {
    return { kind: "formula", token };
  }
  const formula = formulaToUnicode(parsed.formula);
  if (overrideMatch) {
    const dHf = Number(overrideMatch[1]);
    const label = state ? labelOf(formula, state) : formula;
    const entry: TableSpecies = {
      raw: body.trim(),
      state: state ?? "g",
      name: "",
      dHf,
      formula,
      label,
    };
    return {
      coefficient,
      formula,
      state,
      entry,
      custom: true,
      parsed: parsed.formula,
    };
  }
  const found = lookup(formula, state);
  if (Array.isArray(found)) {
    return { kind: "ambiguous", token, states: found };
  }
  return {
    coefficient,
    formula,
    state,
    entry: found === "none" ? null : found,
    custom: false,
    parsed: parsed.formula,
  };
}

function parseSide(text: string): Side {
  const terms: Term[] = [];
  const problems: TermProblem[] = [];
  for (const token of text.split(SIDE_SPLIT)) {
    if (token.trim() === "") {
      continue;
    }
    const result = parseTerm(token);
    if ("coefficient" in result && "parsed" in result) {
      terms.push(result);
      if (result.entry === null) {
        problems.push({ kind: "unknown", token: token.trim() });
      }
    } else {
      problems.push(result as TermProblem);
    }
  }
  return { terms, problems };
}

export interface Equation {
  reactants: Side;
  products: Side;
}

/** Split at the arrow and read both sides; null when there is no arrow. */
export function parseEquation(text: string): Equation | null {
  const match = ARROW.exec(text);
  if (!match) {
    return null;
  }
  return {
    reactants: parseSide(text.slice(0, match.index)),
    products: parseSide(text.slice(match.index + match[0].length)),
  };
}

/** Add a table species to the reactant or product side of the text. */
export function appendSpecies(
  text: string,
  side: "reactants" | "products",
  species: TableSpecies
): string {
  const label = species.label;
  const match = ARROW.exec(text);
  const left = (match ? text.slice(0, match.index) : text).trim();
  const right = match ? text.slice(match.index + match[0].length).trim() : "";
  const join = (existing: string, add: string) =>
    existing === "" ? add : `${existing} + ${add}`;
  if (side === "reactants") {
    return `${join(left, label)} -> ${right}`.trimEnd();
  }
  return `${left} -> ${join(right, label)}`.trimStart();
}

/* ------------------------------------------------------------ the arithmetic */

export interface Line {
  term: Term;
  /** coefficient × ΔfH°, kJ. */
  contribution: number;
}

export interface ReactionEnthalpy {
  reactants: Line[];
  products: Line[];
  /** Σ for each side, kJ. */
  reactantSum: number;
  productSum: number;
  /** ΔH°rxn for the reaction as written, kJ. */
  delta: number;
}

function lines(side: Side): Line[] {
  return side.terms.map((term) => ({
    term,
    contribution: term.coefficient * (term.entry?.dHf ?? 0),
  }));
}

const sum = (items: readonly Line[]) =>
  items.reduce((total, item) => total + item.contribution, 0);

/** ΔH°rxn, or null while any species is missing from the table or a side is empty. */
export function reactionEnthalpy(equation: Equation): ReactionEnthalpy | null {
  const { reactants, products } = equation;
  const allFound = [...reactants.terms, ...products.terms].every(
    (term) => term.entry !== null
  );
  const unreadable = reactants.problems.length + products.problems.length;
  if (
    !allFound ||
    unreadable > 0 ||
    reactants.terms.length === 0 ||
    products.terms.length === 0
  ) {
    return null;
  }
  const r = lines(reactants);
  const p = lines(products);
  return {
    reactants: r,
    products: p,
    reactantSum: sum(r),
    productSum: sum(p),
    delta: sum(p) - sum(r),
  };
}

/* ------------------------------------------------------------- balance check */

export interface BalanceReport {
  balanced: boolean;
  /** Elements (or "charge") whose totals differ. */
  differing: string[];
}

const TOLERANCE = 1e-9;

function totals(terms: readonly Term[]): Map<string, number> {
  const out = new Map<string, number>();
  for (const term of terms) {
    for (const { symbol, count } of term.parsed.counts) {
      out.set(symbol, (out.get(symbol) ?? 0) + count * term.coefficient);
    }
    out.set(
      "charge",
      (out.get("charge") ?? 0) + term.parsed.charge * term.coefficient
    );
  }
  return out;
}

export function balanceReport(equation: Equation): BalanceReport {
  const left = totals(equation.reactants.terms);
  const right = totals(equation.products.terms);
  const keys = new Set([...left.keys(), ...right.keys()]);
  const differing = [...keys].filter(
    (key) => Math.abs((left.get(key) ?? 0) - (right.get(key) ?? 0)) > TOLERANCE
  );
  return { balanced: differing.length === 0, differing };
}

/* --------------------------------------------------------- per-species heat */

/** Molar mass of a parsed species in g/mol. */
export function termMolarMass(term: Term): number {
  return molarMassOf(countAtoms(term.parsed.parts.flatMap((p) => p.nodes)))
    .total;
}

/** Heat for `moles` of one species taking part: ΔH° × moles ÷ its coefficient, kJ. */
export function heatForAmount(
  delta: number,
  coefficient: number,
  moles: number
): number {
  return (delta * moles) / coefficient;
}

/* ----------------------------------------------------------------- Hess's law */

export interface HessStep {
  /** ΔH of the step as written, kJ. */
  delta: number;
  /** What the step is multiplied by (2 for doubling, 0.5 for halving). */
  multiplier: number;
  /** True when the step is run backwards, which flips the sign. */
  reversed: boolean;
}

/** One step's contribution: ±multiplier × ΔH. */
export function hessContribution(step: HessStep): number {
  return (step.reversed ? -1 : 1) * step.multiplier * step.delta;
}

export function hessTotal(steps: readonly HessStep[]): number {
  return steps.reduce((total, step) => total + hessContribution(step), 0);
}

export const KJ_PER_KCAL = 4.184;

export interface Preset {
  id: string;
  /** The equation text, ready to load. */
  equation: string;
}

export const REACTION_PRESETS: readonly Preset[] = [
  { id: "methane", equation: "CH4(g) + 2 O2(g) -> CO2(g) + 2 H2O(l)" },
  { id: "glucose", equation: "C6H12O6(s) + 6 O2(g) -> 6 CO2(g) + 6 H2O(l)" },
  { id: "ammonia", equation: "N2(g) + 3 H2(g) -> 2 NH3(g)" },
  { id: "limestone", equation: "CaCO3(s) -> CaO(s) + CO2(g)" },
  { id: "thermite", equation: "2 Al(s) + Fe2O3(s) -> Al2O3(s) + 2 Fe(s)" },
  { id: "neutralisation", equation: "H+(aq) + OH-(aq) -> H2O(l)" },
];
